import { useState } from 'react';
import { validateCardNumber, validateConfirmCardNumber, validateOnlyNumbers } from '@/features/card/components/ActivateCardModal/activateCard.validators';
import { useActivateCard } from '@/features/onboarding/hooks/useActivateCard';
import { useSetPassword } from '@/features/onboarding/hooks/useSetPassword';
import { hashPassword } from '@chikara/utils';
import { t } from '@/i18n';

export const CARD_MAX_LENGTH = 19;
export const PASSWORD_MIN_LENGTH = 8;


export const useOnboardingStep = ({ user, onSuccess, reference, onPasswordSet }: { user?: any, onSuccess: () => void, reference?: string, onPasswordSet?: (password: string) => void }) => {
    const [error, setError] = useState<string | null>(null);
    const [state, setState] = useState<any>({
        step: "",
        cardNumber: '',
        confirmCardNumber: '',
        password: '',
        confirmPassword: '',
        errorCount: 0,
        isFailed: false,
        errors: {},
    });

    const { activateCard, getAccount } = useActivateCard();
    const { setupPassword } = useSetPassword();

    /* ---------------- Setup Password ---------------- */

    const isLengthValid = state.password.length >= PASSWORD_MIN_LENGTH;
    const hasNumber = /\d/.test(state.password);
    const hasSpecial = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(state.password);
    const hasUppercase = /[A-Z]/.test(state.password);

    const isValidPassword = isLengthValid && hasNumber && hasSpecial && hasUppercase;
    const isMatchValid = state.password.length > 0 && state.password === state.confirmPassword;
    const isFormValid = isValidPassword && isMatchValid;

    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setState((prev: any) => ({
            ...prev,
            password: value,
            errors: {
                ...prev.errors
            },
        }));
    };

    const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setState((prev: any) => ({
            ...prev,
            confirmPassword: value,
            errors: {
                ...prev.errors
            },
        }));
    };

    const handleSetupPassword = async () => {
        if (!isFormValid) return;

        const hashedPassword = await hashPassword(state.password);
        if (!hashedPassword || !reference) return;

        // accept (mintea password_token) + confirm en un mismo flujo: el token
        // se obtiene fresh dentro de setupPassword justo antes de confirmar.
        const success = await setupPassword(reference, hashedPassword);
        if (!success) return;

        // El auto-login ocurre al final del flujo de tarjeta (ver OnboardingWizard),
        // por eso surfaceamos el password en claro para reutilizarlo en el login.
        onPasswordSet?.(state.password);
        onSuccess();
    };


    /* ---------------- Card Number ---------------- */

    const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = validateOnlyNumbers(e.target.value);
        const error = validateCardNumber(value);

        setState((prev: any) => {
            const confirmError = prev.confirmCardNumber
                ? validateConfirmCardNumber(prev.confirmCardNumber, value)
                : prev.errors.confirmCardNumber;

            return {
                ...prev,
                cardNumber: value,
                errors: {
                    ...prev.errors,
                    cardNumber: error,
                    confirmCardNumber: confirmError
                },
            };
        });
    };

    const handleConfirmCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = validateOnlyNumbers(e.target.value);
        const error = validateConfirmCardNumber(value, state.cardNumber);

        setState((prev: any) => ({
            ...prev,
            confirmCardNumber: value,
            errors: { ...prev.errors, confirmCardNumber: error },
        }));
    };

    /* ---------------- Activate Card ---------------- */

    const handleSubmitActivateCard = async () => {
        const validationError = validate();
        if (validationError) {
            setError(validationError);
            return;
        }
        const accountData = await getAccount(user.uuid);
        const accountUuid = accountData?.accounts?.[0].uuid

        if (!accountUuid) {
            setError(t('onboarding.activateCard.errors.accountNotFound'));
            return;
        }

        const payload = {
            card_number: state.cardNumber.replace(/\s/g, ''),
            account: accountUuid,
            card_in_hand: true,
        };

        const success = await activateCard(payload);
        if (success) {
            onSuccess();
        } else {
            setState((prev: any) => {
                const nextErrorCount = (prev.errorCount || 0) + 1;
                return {
                    ...prev,
                    errorCount: nextErrorCount,
                    isFailed: nextErrorCount >= 3,
                };
            });
        }
    };

    const validate = () => {
        if (!state.cardNumber) return t('onboarding.activateCard.errors.panRequired');
        return null;
    };

    return {
        state,
        CARD_MAX_LENGTH,
        error,
        isLengthValid,
        hasUppercase,
        hasNumber,
        hasSpecial,
        isFormValid,
        handleSetupPassword,
        handlePasswordChange,
        handleConfirmPasswordChange,
        handleCardNumberChange,
        handleConfirmCardNumberChange,
        handleSubmitActivateCard,
        setState,
    };
};
